import { recipeWriteVersion, StaleRecipeWriteError } from '../../../../../services/recipeWriteConflict';
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Loader2, Pencil, SlidersHorizontal, X } from "lucide-react";
import { primaryText, greyTextColor, borderPrimary } from "../../../../../styling/stylingHelper";
import { ReviewIngredient, SubrecipeReviewItem } from "../../../../../types/reviewTypes";
import { CostPriceIngredient } from "../../../../../services/costPrice/costPriceService";
import { Subrecipe, updateSubrecipe } from "../../../../../services/subrecipes/subrecipesService";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import { useSubrecipeReviewStore } from "../../../../../stores/appSubrecipeReviewStore";
import { useOrderHistory } from "../../../../../stores/appOrderHistoryStore";
import { useCostPriceStore } from "../../../../../stores/appCostPriceStore";
import { useAppUserDefaultStore } from "../../../../../stores/appUserDefaultStore";
import { calculateCurrentPrice, calculateSubrecipeUnitCost } from "../../../../../utils/costPriceUtils";
import { getPortionsUnitDisplay } from "../../../../../utils/ingredientGrouping";
import { eur } from "../../../../orderhistory/utils";
import IngredientReviewRow from "../IngredientReviewRow";
import { matchesSubrecipeRow } from "../reviewUtils";
import { subrecipeReviewAdapter } from "./subrecipeReviewAdapter";

interface SubrecipeReviewCardProps {
    item: SubrecipeReviewItem;
    // Live subrecipe from the store (undefined when it was deleted meanwhile).
    liveSubrecipe?: Subrecipe;
    defaultWastePercentage: number;
    // Opens the full subrecipe editor slide-over — waste %, extra ingredients,
    // nesting: everything the quick review actions deliberately don't cover.
    onOpenEditor?: () => void;
}

const PORTIONS_UNITS = ['p', 'g', 'ml'] as const;

/**
 * One subrecipe in the import review. Same anatomy as DishReviewCard, with the
 * dish's menu-price stats replaced by the thing that is actually uncertain here:
 * the YIELD. The importer sums the g/ml lines to guess it and falls back to a
 * single portion when the document only had piece amounts — and every
 * cost-per-portion, plus every dish that later nests this subrecipe, divides by
 * that number. So it is editable inline and flagged while it is still a guess.
 */
const SubrecipeReviewCard: React.FC<SubrecipeReviewCardProps> = ({
    item,
    liveSubrecipe,
    defaultWastePercentage,
    onOpenEditor,
}) => {
    const { t } = useTranslation();
    const orders = useOrderHistory(s => s.orderHistoryList);
    const customIngredients = useCostPriceStore(s => s.customIngredients);
    const subrecipesById = useSubrecipesStore(s => s.subrecipesById);
    const reloadSubrecipes = useSubrecipesStore(s => s.reload);
    const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
    const updatePortions = useSubrecipeReviewStore(s => s.updatePortions);

    const [isEditingYield, setIsEditingYield] = useState(false);
    const [yieldVersion, setYieldVersion] = useState<string>();
    const [portionsInput, setPortionsInput] = useState("");
    const [unitInput, setUnitInput] = useState(item.portionsUnit);
    const [isSavingYield, setIsSavingYield] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const portions = liveSubrecipe?.portions ?? item.portions;
    const portionsUnit = liveSubrecipe?.portionsUnit ?? item.portionsUnit;

    // Live cost of one portion unit, and of one full yield. Recurses into nested
    // subrecipes, so it is memoized against the data it reads.
    const unitCost = useMemo(() => (liveSubrecipe
        ? calculateSubrecipeUnitCost(
            liveSubrecipe,
            orders,
            customIngredients,
            showExVatOrders,
            subrecipesById,
            defaultWastePercentage
        )
        : undefined
    ), [liveSubrecipe, orders, customIngredients, showExVatOrders, subrecipesById, defaultWastePercentage]);

    const totalCost = unitCost !== undefined ? unitCost * portions : undefined;

    /**
     * Live rows keyed by their own id, each adapted to the shape the shared
     * ingredient row prices from — a subrecipe row carries no `currentPrice` of
     * its own, so it is resolved here through the same lookup the cost math uses.
     * Nested rows are skipped: this flow never produces one.
     */
    const enrichedRowById = useMemo(() => {
        const map = new Map<string, CostPriceIngredient>();
        (liveSubrecipe?.ingredients ?? [])
            .filter(row => !row.nestedSubrecipeId)
            .forEach(row => {
                const shaped: CostPriceIngredient = {
                    id: row.id,
                    articleId: row.articleId ?? '',
                    productName: row.productName ?? '',
                    quantityNeeded: row.quantityNeeded,
                    quantityInfo: row.quantityInfo ?? { stdAmount: 0, stdMultiplier: 0, stdUnit: '' },
                    platform: row.platform,
                    customPlatformName: row.customPlatformName,
                    custom: row.isCustom,
                    wastePercentage: row.wastePercentage,
                } as CostPriceIngredient;
                const { currentPrice } = calculateCurrentPrice(shaped, orders, customIngredients, showExVatOrders);
                map.set(row.id, { ...shaped, currentPrice });
            });
        return map;
    }, [liveSubrecipe, orders, customIngredients, showExVatOrders]);

    /** The enriched row a report ingredient points at, matched on the real subrecipe row. */
    const enrichedRowFor = (ingredient: ReviewIngredient): CostPriceIngredient | undefined => {
        const row = liveSubrecipe?.ingredients.find(candidate => matchesSubrecipeRow(candidate, ingredient));
        return row ? enrichedRowById.get(row.id) : undefined;
    };

    // Amber while the yield is still the importer's guess, neutral once it is the
    // reviewer's own number. Built as one complete class string per state so both
    // carry their own dark: counterpart.
    const yieldBoxClass = item.portionsWasGuessed
        ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'
        : `${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark`;

    const startEditYield = () => {
        setYieldVersion(liveSubrecipe ? recipeWriteVersion(liveSubrecipe) : undefined);
        setPortionsInput(portions.toString());
        setUnitInput(portionsUnit);
        setIsEditingYield(true);
        setError(null);
    };

    const handleSaveYield = async () => {
        const nextPortions = parseFloat(portionsInput);
        if (!Number.isFinite(nextPortions) || nextPortions <= 0) return;

        setIsSavingYield(true);
        setError(null);
        try {
            await updateSubrecipe({ id: item.subrecipeId, expectedUpdatedAt: yieldVersion, portions: nextPortions, portionsUnit: unitInput });
            await reloadSubrecipes();
            updatePortions(item.subrecipeId, nextPortions, unitInput);
            setIsEditingYield(false);
        } catch (err) {
            console.error('Failed to update subrecipe yield:', err);
            setError(err instanceof StaleRecipeWriteError ? err.message : t('costPrice.subrecipeReview.updateFailed'));
            if (err instanceof StaleRecipeWriteError) setIsEditingYield(false);
        } finally {
            setIsSavingYield(false);
        }
    };

    return (
        <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-5 space-y-4`}>
            {/* Subrecipe header */}
            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`text-[20px] leading-7 font-bold ${primaryText}`}>{item.name}</h3>
                    {onOpenEditor && (
                        <button
                            type="button"
                            onClick={onOpenEditor}
                            className={`ml-auto flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-lg border ${borderPrimary} hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark ${primaryText}`}
                        >
                            <SlidersHorizontal className="h-4 w-4" />
                            {t('costPrice.subrecipeReview.openEditor')}
                        </button>
                    )}
                </div>
                {!liveSubrecipe && (
                    <p className="text-[13px] text-red-600 dark:text-red-400 mt-1">
                        {t('costPrice.subrecipeReview.subrecipeDeleted')}
                    </p>
                )}
            </div>

            {/* Yield — the number the import had to guess, front and centre */}
            <div className={`rounded-lg border px-3 py-2.5 ${yieldBoxClass}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <p className={`text-[12px] ${greyTextColor}`}>{t('costPrice.subrecipeReview.yieldLabel')}</p>
                        {isEditingYield ? (
                            <div className="flex items-center gap-2 mt-1">
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    autoFocus
                                    value={portionsInput}
                                    onChange={(e) => setPortionsInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveYield();
                                        if (e.key === 'Escape') setIsEditingYield(false);
                                    }}
                                    className={`w-24 rounded-md border bg-background dark:bg-background-dark px-2 h-8 text-[14px] ${primaryText} outline-none border-borderGrey dark:border-borderGrey-dark`}
                                />
                                <select
                                    value={unitInput}
                                    onChange={(e) => setUnitInput(e.target.value)}
                                    className={`rounded-md border bg-background dark:bg-background-dark px-2 h-8 text-[14px] ${primaryText} outline-none border-borderGrey dark:border-borderGrey-dark`}
                                >
                                    {PORTIONS_UNITS.map(unit => (
                                        <option key={unit} value={unit}>{getPortionsUnitDisplay(unit)}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={handleSaveYield}
                                    disabled={isSavingYield || !(parseFloat(portionsInput) > 0)}
                                    title={t('costPrice.subrecipeReview.saveYield')}
                                    className="p-1.5 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 disabled:opacity-50"
                                >
                                    {isSavingYield ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsEditingYield(false)}
                                    title={t('costPrice.subrecipeReview.cancel')}
                                    className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark ${greyTextColor}`}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={startEditYield}
                                className={`mt-0.5 flex items-center gap-2 text-[16px] font-semibold ${primaryText}`}
                            >
                                <span>{portions} {getPortionsUnitDisplay(portionsUnit)}</span>
                                <Pencil className={`h-3.5 w-3.5 ${greyTextColor}`} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-4 flex-shrink-0">
                        <div>
                            <p className={`text-[12px] ${greyTextColor}`}>
                                {t('costPrice.subrecipeReview.costPerUnit', { unit: getPortionsUnitDisplay(portionsUnit) })}
                            </p>
                            <p className={`text-[16px] font-semibold ${primaryText}`}>
                                {unitCost !== undefined ? eur(unitCost) : '—'}
                            </p>
                        </div>
                        <div>
                            <p className={`text-[12px] ${greyTextColor}`}>{t('costPrice.subrecipeReview.totalCost')}</p>
                            <p className={`text-[16px] font-semibold ${primaryText}`}>
                                {totalCost !== undefined ? eur(totalCost) : '—'}
                            </p>
                        </div>
                    </div>
                </div>

                {item.portionsWasGuessed && !isEditingYield && (
                    <p className="mt-2 flex items-start gap-1.5 text-[12px] text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        {t('costPrice.subrecipeReview.yieldGuessed')}
                    </p>
                )}
                {error && <p className="mt-1 text-[13px] text-red-600 dark:text-red-400">{error}</p>}
            </div>

            {/* What this run actually wrote */}
            <div className={`rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 py-2`}>
                <p className={`text-[13px] ${primaryText}`}>
                    {t('costPrice.subrecipeReview.runImpact', {
                        matched: item.ingredientsWritten,
                        total: item.ingredients.length,
                    })}
                </p>
            </div>

            {/* Ingredient rows */}
            <div>
                <p className={`text-[14px] font-semibold ${primaryText} mb-2`}>
                    {t('costPrice.subrecipeReview.ingredients')}
                </p>
                {item.ingredients.length === 0 ? (
                    <p className={`text-[13px] ${greyTextColor}`}>
                        {t('costPrice.subrecipeReview.noIngredients')}
                    </p>
                ) : (
                    <div className="space-y-2">
                        {item.ingredients.map((ingredient, index) => (
                            <IngredientReviewRow
                                key={`${item.subrecipeId}-${index}`}
                                ingredient={ingredient}
                                ingredientIndex={index}
                                itemId={item.subrecipeId}
                                enrichedRow={enrichedRowFor(ingredient)}
                                defaultWastePercentage={defaultWastePercentage}
                                adapter={subrecipeReviewAdapter}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubrecipeReviewCard;
